import React, { useEffect, useMemo, useState } from 'react';
import {
  NavLink,
  Outlet,
  Link,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  UserRound,
  Store,
  Truck,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  Shield,
  ChevronRight,
  Languages
} from 'lucide-react';
import { useAuth } from '@/shared/contexts';
import ThemeToggle from '@/shared/components/ThemeToggle';

const NAV = [
  {
    to: '/super-admin',
    end: true,
    label: 'Dashboard',
    icon: LayoutDashboard
  },
  {
    to: '/super-admin/restaurants',
    end: false,
    label: 'Restaurants',
    icon: Store
  },
  {
    to: '/super-admin/suppliers',
    end: false,
    label: 'Suppliers',
    icon: Truck
  },
  {
    to: '/super-admin/orders',
    end: false,
    label: 'Orders',
    icon: ShoppingCart
  },
  {
    to: '/super-admin/localizations',
    end: false,
    label: 'Languages',
    icon: Languages
  },
  {
    to: '/super-admin/profile',
    end: false,
    label: 'Settings',
    icon: UserRound
  }
] as const;

const SIDEBAR_KEY = 'superAdminSidebarCollapsed';

type Crumb = { label: string; to?: string };

const SuperAdminBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const { accessToken } = useAuth();
  const [supplierLabel, setSupplierLabel] = useState<string | null>(null);

  const supplierId = params.supplierId;
  const restaurantId = params.restaurantId;

  useEffect(() => {
    if ((!supplierId && !restaurantId) || !accessToken) {
      setSupplierLabel(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { superAdminApi } = await import(
          '@/pages/SuperAdmin/api/superAdminApi'
        );
        if (location.pathname.startsWith('/super-admin/restaurants/') && restaurantId) {
          const res = await superAdminApi.getRestaurantDetail(
            accessToken,
            restaurantId
          );
          if (cancelled) return;
          setSupplierLabel(
            res.data.restaurant.businessName ||
              res.data.restaurant.email ||
              'Restaurant'
          );
          return;
        }
        if (location.pathname.startsWith('/super-admin/suppliers/') && supplierId) {
          const res = await superAdminApi.getSupplierDetail(
            accessToken,
            supplierId
          );
          if (cancelled) return;
          setSupplierLabel(
            res.data.supplier.businessName ||
              res.data.supplier.email ||
              'Supplier'
          );
          return;
        }
        if (supplierId) {
          const res = await superAdminApi.listSuppliers(accessToken);
          if (cancelled) return;
          const match = res.data.find(s => s.id === supplierId);
          setSupplierLabel(
            match?.businessName || match?.email || 'Supplier order'
          );
        }
      } catch {
        if (!cancelled) setSupplierLabel('…');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supplierId, restaurantId, accessToken, location.pathname]);

  const crumbs: Crumb[] = useMemo(() => {
    const path = location.pathname;
    const items: Crumb[] = [{ label: 'Super Admin', to: '/super-admin' }];

    if (path.startsWith('/super-admin/restaurants')) {
      items.push({
        label: 'Restaurants',
        to: restaurantId ? '/super-admin/restaurants' : undefined
      });
      if (restaurantId) {
        items.push({ label: supplierLabel || '…' });
      }
      return items;
    }

    if (path.startsWith('/super-admin/suppliers')) {
      items.push({
        label: 'Suppliers',
        to: supplierId ? '/super-admin/suppliers' : undefined
      });
      if (supplierId) {
        const onSupplierOrders = path.endsWith('/orders');
        const onCatalog = path.endsWith('/catalog');
        items.push({
          label: supplierLabel || '…',
          to:
            onSupplierOrders || onCatalog
              ? `/super-admin/suppliers/${supplierId}`
              : undefined
        });
        if (onSupplierOrders) {
          items.push({ label: 'Place order' });
        }
        if (onCatalog) {
          items.push({ label: 'Catalog' });
        }
      }
      return items;
    }

    if (path.startsWith('/super-admin/profile')) {
      items.push({ label: 'Settings' });
      return items;
    }

    if (path.startsWith('/super-admin/localizations')) {
      items.push({ label: 'Languages' });
      return items;
    }

    if (path.startsWith('/super-admin/orders')) {
      items.push({
        label: 'Orders',
        to: supplierId ? '/super-admin/orders' : undefined
      });
      if (supplierId) {
        items.push({ label: supplierLabel || '…' });
      }
      return items;
    }

    items.push({ label: 'Dashboard' });
    return items;
  }, [location.pathname, supplierId, restaurantId, supplierLabel]);

  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-text-tertiary"
    >
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <React.Fragment key={`${crumb.label}-${i}`}>
            {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-text-muted" />}
            {crumb.to && !isLast ? (
              <Link
                to={crumb.to}
                className="hover:text-[#006B82] transition-colors"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={
                  isLast ? 'font-medium text-text-primary' : undefined
                }
              >
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export const SuperAdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const pageTitle =
    NAV.find(n =>
      n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)
    )?.label ?? 'Super Admin';

  const sidebarWidth = collapsed ? 'w-[72px]' : 'w-64';

  const NavItems = ({ compact }: { compact?: boolean }) => (
    <nav className="flex flex-col gap-1 px-2">
      {NAV.map(({ to, end, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          title={compact ? label : undefined}
          className={({ isActive }) => {
            const activeOrdersChild =
              to === '/super-admin/orders' &&
              location.pathname.startsWith('/super-admin/orders');
            const active =
              isActive ||
              (to === '/super-admin/orders' && activeOrdersChild && !end);
            return [
              'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
              compact ? 'justify-center px-0' : '',
              active
                ? 'bg-[#006B82] text-white shadow-sm'
                : 'text-text-secondary hover:bg-background-hover hover:text-text-primary'
            ].join(' ');
          }}
        >
          <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
          {!compact && <span>{label}</span>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background-secondary text-text-primary">
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border-primary bg-background-card transition-all duration-200',
          sidebarWidth,
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        ].join(' ')}
      >
        <div
          className={[
            'flex h-16 items-center border-b border-border-primary px-3',
            collapsed ? 'justify-center' : 'justify-between gap-2'
          ].join(' ')}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#006B82] text-white">
              <Shield className="h-5 w-5" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-text-primary">
                  Gio
                </p>
                <p className="truncate text-[11px] uppercase tracking-wide text-text-tertiary">
                  Super Admin
                </p>
              </div>
            )}
          </div>
          {!collapsed && (
            <button
              type="button"
              className="hidden lg:inline-flex rounded-md p-1.5 text-text-tertiary hover:bg-background-hover"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          <NavItems compact={collapsed} />
        </div>

        <div className="border-t border-border-primary p-3 space-y-2">
          {collapsed ? (
            <button
              type="button"
              className="hidden lg:flex w-full items-center justify-center rounded-lg p-2 text-text-tertiary hover:bg-background-hover"
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleLogout}
            title="Log out"
            className={[
              'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-text-secondary hover:bg-state-error-bg hover:text-accent-error transition-colors',
              collapsed ? 'justify-center px-0' : ''
            ].join(' ')}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!collapsed && <span>Log out</span>}
          </button>
        </div>
      </aside>

      <div
        className={[
          'min-h-screen transition-[padding] duration-200',
          collapsed ? 'lg:pl-[72px]' : 'lg:pl-64'
        ].join(' ')}
      >
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border-primary bg-background-card px-4 backdrop-blur supports-[backdrop-filter]:bg-background-card/90">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="inline-flex rounded-md p-2 text-text-secondary hover:bg-background-hover lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-text-primary">
                {pageTitle}
              </h1>
              <p className="hidden sm:block text-xs text-text-tertiary">
                Platform operations
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <ThemeToggle />
            <Link
              to="/super-admin/profile"
              className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-background-hover transition-colors"
              title="Settings"
            >
              <div className="hidden sm:flex flex-col items-end leading-tight">
                <span className="text-sm font-medium text-text-primary truncate max-w-[200px]">
                  {user?.business_name ||
                    [user?.first_name, user?.last_name]
                      .filter(Boolean)
                      .join(' ') ||
                    'Super Admin'}
                </span>
                <span className="text-xs text-text-tertiary truncate max-w-[200px]">
                  {user?.email}
                </span>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#006B82]/10 text-[#006B82] text-sm font-semibold">
                {(user?.email?.[0] || 'S').toUpperCase()}
              </div>
            </Link>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          <SuperAdminBreadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
};
